import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    Eye,
    LayoutGrid,
    List,
    Package,
    PackagePlus,
    PackageSearch,
    Search,
} from "lucide-react";
import { toast } from "sonner";

import {
    fetchProducts,
    fallbackProducts,
    filterProductsByKeywords,
    PRODUCTS_FALLBACK_ERROR_MESSAGE,
    PRODUCTS_FALLBACK_TOAST_TITLE,
    PRODUCTS_FALLBACK_TOAST_DESC,
    type ProductRecord,
} from "@/lib/api";
import { EmptyState } from "@/components/feedback/EmptyState.tsx";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { PageBreadcrumb } from "@/components/layout/PageBreadcrumb.tsx";
import { CardSkeleton } from "@/components/loading/CardSkeleton.tsx";
import { TableSkeleton } from "@/components/loading/TableSkeleton.tsx";
import { ProductPreviewDialog } from "@/components/prodotti/ProductPreviewDialog.tsx";
import { SelectCustomerModal } from "@/components/prodotti/SelectCustomerModal.tsx";
import { Button } from "@/components/ui/button";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";

// ----------------------------------------------------------------------------

type ViewMode = "grid" | "table";

const COLOR_OPTIONS = [
    "Nero",
    "Bianco",
    "Ottone",
    "Bronzo",
    "Cromo",
    "Grigio",
    "Sabbia",
    "Antracite",
];

const TEMPERATURE_OPTIONS = ["2700K", "3000K", "4000K", "5000K", "6000K"];

function normalizeProductText(product: ProductRecord): string {
    return [
        product.code,
        product.sku,
        product.name,
        product.description,
        product.brandName,
        ...(product.categories ?? []),
        ...(product.tags ?? []),
    ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
}

function getColorBadges(product: ProductRecord): string[] {
    const text = normalizeProductText(product);
    const matches = COLOR_OPTIONS.filter((color) =>
        text.includes(color.toLowerCase()),
    );

    return matches.slice(0, 3);
}

function getTemperatureBadges(product: ProductRecord): string[] {
    const text = normalizeProductText(product);
    const matches = TEMPERATURE_OPTIONS.filter((temperature) =>
        text.includes(temperature.toLowerCase()),
    );

    return matches.slice(0, 3);
}

function getVariantSummary(product: ProductRecord): string[] {
    const colorBadges = getColorBadges(product);
    const temperatureBadges = getTemperatureBadges(product);

    if (colorBadges.length === 0 && temperatureBadges.length === 0) {
        return ["Varianti da verificare"];
    }

    return [...colorBadges, ...temperatureBadges];
}

interface ProdottiProps {
    className?: string;
}

export function Prodotti(props: ProdottiProps) {
    // -------------------------------------
    // Props destructuring
    // -------------------------------------

    const { className } = props;

    // -------------------------------------
    // Hooks (e.g. useState, useMemo ...)
    // -------------------------------------

    const [products, setProducts] = useState<ProductRecord[]>([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [viewMode, setViewMode] = useState<ViewMode>("grid");
    const [isLoadingProducts, setIsLoadingProducts] = useState(true);
    const [productsError, setProductsError] = useState<string | null>(null);
    const [selectedProduct, setSelectedProduct] =
        useState<ProductRecord | null>(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [productForCustomerSelect, setProductForCustomerSelect] =
        useState<ProductRecord | null>(null);
    const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
    const filteredProducts = products;

    const [imageLoadErrors, setImageLoadErrors] = useState<
        Record<string, boolean>
    >({});

    // Pagination states from backend API
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [productsPerPage, setProductsPerPage] = useState(50);

    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // -------------------------------------
    // Effects
    // -------------------------------------

    // Sync state from URL search params on mount or URL change
    useEffect(() => {
        const searchFromUrl = searchParams.get("search") ?? "";
        const pageFromUrl = parseInt(searchParams.get("page") ?? "1", 10) || 1;

        setSearchQuery((currentValue) =>
            currentValue === searchFromUrl ? currentValue : searchFromUrl,
        );
        setCurrentPage((currentPageVal) =>
            currentPageVal === pageFromUrl ? currentPageVal : pageFromUrl,
        );
    }, [searchParams]);

    useEffect(() => {
        let isMounted = true;

        async function loadProducts(): Promise<void> {
            setIsLoadingProducts(true);
            setProductsError(null);

            try {
                const res = await fetchProducts(searchQuery, currentPage, 50);

                if (!isMounted) {
                    return;
                }

                setProducts(res.products);
                setTotalPages(res.totalPages);
                setTotalItems(res.totalItems);
                setProductsPerPage(res.productsPerPage);
            } catch (error) {
                console.error("[Prodotti] Fetch failed:", error);

                if (!isMounted) {
                    return;
                }

                const filteredFallback = filterProductsByKeywords(
                    fallbackProducts,
                    searchQuery,
                );
                setProducts(filteredFallback);
                setTotalPages(1);
                setTotalItems(filteredFallback.length);
                setProductsPerPage(50);
                setProductsError(PRODUCTS_FALLBACK_ERROR_MESSAGE);
                toast.warning(PRODUCTS_FALLBACK_TOAST_TITLE, {
                    id: "catalog-products-fallback",
                    description: PRODUCTS_FALLBACK_TOAST_DESC,
                });
            } finally {
                if (isMounted) {
                    setIsLoadingProducts(false);
                }
            }
        }

        void loadProducts();

        return () => {
            isMounted = false;
        };
    }, [searchQuery, currentPage]);

    // -------------------------------------
    // Component functions
    // -------------------------------------

    function updateUrlParams(query: string, pageNum: number) {
        const params = new URLSearchParams();
        if (query.trim()) {
            params.set("search", query.trim());
        }
        if (pageNum > 1) {
            params.set("page", pageNum.toString());
        }
        setSearchParams(params, { replace: true });
    }

    function handleSearchInputChange(newQuery: string) {
        setSearchQuery(newQuery);
        setCurrentPage(1);
        updateUrlParams(newQuery, 1);
    }

    function handlePageChange(newPage: number) {
        const validPage = Math.max(1, Math.min(totalPages, newPage));
        setCurrentPage(validPage);
        updateUrlParams(searchQuery, validPage);
    }

    function handleClearSearch() {
        setSearchQuery("");
        setCurrentPage(1);
        updateUrlParams("", 1);
    }

    function handleOpenPreview(product: ProductRecord) {
        setSelectedProduct(product);
        setIsPreviewOpen(true);
    }

    function handleAddToOrder(product: ProductRecord) {
        setIsPreviewOpen(false);
        setProductForCustomerSelect(product);
        setIsCustomerModalOpen(true);
    }

    function handleConfirmCustomer(product: ProductRecord, clientId: string) {
        setIsCustomerModalOpen(false);
        navigate("/nuovo-ordine", {
            state: {
                preselectedProduct: product,
                selectedClientId: clientId,
            },
        });
        toast.success(`${product.code} pronto per il nuovo ordine`);
    }

    // -------------------------------------
    // Component local variables
    // -------------------------------------

    const selectedProductColors = selectedProduct
        ? getColorBadges(selectedProduct)
        : [];
    const selectedProductTemperatures = selectedProduct
        ? getTemperatureBadges(selectedProduct)
        : [];

    return (
        <DashboardLayout className={className}>
            <div className="w-full">
                <PageBreadcrumb
                    items={[{ label: "Home", to: "/" }, { label: "Prodotti" }]}
                />

                <div className="border-b border-gray-200 mb-6 w-full" />

                <Card className="shadow-sm border-gray-100">
                    <CardHeader className="gap-4">
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                            <div>
                                <CardTitle className="text-2xl font-bold text-gray-900">
                                    Catalogo Prodotti B2B
                                </CardTitle>
                                <CardDescription className="mt-1 text-sm text-gray-500">
                                    Esplora il catalogo, controlla varianti e
                                    aggiungi rapidamente un prodotto a un nuovo
                                    ordine.
                                </CardDescription>
                            </div>

                            <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-1">
                                <Button
                                    variant={
                                        viewMode === "grid"
                                            ? "default"
                                            : "ghost"
                                    }
                                    className={
                                        viewMode === "grid"
                                            ? "bg-[#0D6EFD] hover:bg-blue-700 text-white"
                                            : "text-gray-600"
                                    }
                                    onClick={() => setViewMode("grid")}>
                                    <LayoutGrid className="mr-2 h-4 w-4" />
                                    Grid
                                </Button>
                                <Button
                                    variant={
                                        viewMode === "table"
                                            ? "default"
                                            : "ghost"
                                    }
                                    className={
                                        viewMode === "table"
                                            ? "bg-[#0D6EFD] hover:bg-blue-700 text-white"
                                            : "text-gray-600"
                                    }
                                    onClick={() => setViewMode("table")}>
                                    <List className="mr-2 h-4 w-4" />
                                    Lista
                                </Button>
                            </div>
                        </div>
                    </CardHeader>

                    <CardContent className="space-y-6">
                        {productsError && (
                            <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                                {productsError}
                            </div>
                        )}

                        <div className="flex flex-col gap-4">
                            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                                <div className="relative w-full lg:max-w-md">
                                    <Input
                                        className="w-full pr-10 bg-white border-gray-200"
                                        placeholder="Cerca per SKU, codice o nome prodotto..."
                                        value={searchQuery}
                                        onChange={(event) =>
                                            handleSearchInputChange(
                                                event.target.value,
                                            )
                                        }
                                    />
                                    <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                </div>

                                <div className="text-sm font-medium text-gray-500">
                                    {filteredProducts.length} visibili su{" "}
                                    {totalItems} totali
                                </div>
                            </div>

                            {/* Filter controls row (Commented out per user request)
              <div className="flex flex-wrap items-center gap-3 border-t border-gray-100 pt-3">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  <Filter className="h-3.5 w-3.5" />
                  <span>Filtri:</span>
                </div>

                <select
                  value={selectedBrand}
                  onChange={(e) => setSelectedBrand(e.target.value)}
                  className="h-8 rounded-md border border-gray-200 bg-white px-2.5 text-xs text-gray-700 focus:border-[#0D6EFD] focus:outline-none"
                >
                  <option value="all">
                    Tutti i Brand {availableBrands.length > 0 ? `(${availableBrands.length})` : ""}
                  </option>
                  {availableBrands.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>

                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="h-8 rounded-md border border-gray-200 bg-white px-2.5 text-xs text-gray-700 focus:border-[#0D6EFD] focus:outline-none"
                >
                  <option value="all">
                    Tutte le Categorie {availableCategories.length > 0 ? `(${availableCategories.length})` : ""}
                  </option>
                  {availableCategories.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>

                <select
                  value={selectedColor}
                  onChange={(e) => setSelectedColor(e.target.value)}
                  className="h-8 rounded-md border border-gray-200 bg-white px-2.5 text-xs text-gray-700 focus:border-[#0D6EFD] focus:outline-none"
                >
                  <option value="all">
                    Tutti i Colori {availableColors.length > 0 ? `(${availableColors.length})` : ""}
                  </option>
                  {availableColors.map((col) => (
                    <option key={col} value={col}>
                      {col}
                    </option>
                  ))}
                </select>

                <select
                  value={selectedTemperature}
                  onChange={(e) => setSelectedTemperature(e.target.value)}
                  className="h-8 rounded-md border border-gray-200 bg-white px-2.5 text-xs text-gray-700 focus:border-[#0D6EFD] focus:outline-none"
                >
                  <option value="all">
                    Tutte le Temp. {availableTemperatures.length > 0 ? `(${availableTemperatures.length})` : ""}
                  </option>
                  {availableTemperatures.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              */}
                        </div>

                        {isLoadingProducts && viewMode === "grid" && (
                            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 2xl:grid-cols-3">
                                {Array.from({ length: 6 }, (_, index) => (
                                    <Card
                                        key={`catalog-grid-skeleton-${index}`}
                                        className="border-gray-100 shadow-sm">
                                        <CardContent className="p-0">
                                            <CardSkeleton count={1} />
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        )}

                        {isLoadingProducts && viewMode === "table" && (
                            <TableSkeleton
                                columns={4}
                                rows={8}
                                mobileCards={4}
                            />
                        )}

                        {!isLoadingProducts &&
                            filteredProducts.length === 0 && (
                                <EmptyState
                                    icon={PackageSearch}
                                    title="Nessun prodotto trovato"
                                    description="Prova a cambiare ricerca o a usare un SKU, codice o nome prodotto piu breve."
                                    actionLabel="Pulisci ricerca"
                                    onAction={handleClearSearch}
                                    iconClassName="bg-blue-50 text-blue-600"
                                />
                            )}

                        {!isLoadingProducts &&
                            filteredProducts.length > 0 &&
                            viewMode === "grid" && (
                                <div className="grid grid-cols-1 gap-5 md:grid-cols-2 2xl:grid-cols-3">
                                    {filteredProducts.map((product) => {
                                        const colorBadges =
                                            getColorBadges(product);
                                        const temperatureBadges =
                                            getTemperatureBadges(product);

                                        return (
                                            <Card
                                                key={product.code}
                                                className="overflow-hidden rounded-2xl border-gray-100 shadow-sm">
                                                <div className="aspect-[5/4] border-b border-gray-100 bg-gray-50">
                                                    {product.imageUrl &&
                                                    !imageLoadErrors[
                                                        product.code
                                                    ] ? (
                                                        <img
                                                            src={
                                                                product.imageUrl
                                                            }
                                                            alt={
                                                                product.name ||
                                                                product.code
                                                            }
                                                            className="h-full w-full object-cover"
                                                            onError={() => {
                                                                setImageLoadErrors(
                                                                    (prev) => ({
                                                                        ...prev,
                                                                        [product.code]: true,
                                                                    }),
                                                                );
                                                            }}
                                                        />
                                                    ) : (
                                                        <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-gradient-to-br from-gray-100 to-gray-200 text-gray-500 p-4 text-center">
                                                            <span className="text-sm font-semibold text-gray-700">
                                                                {product.name ||
                                                                    product.code}
                                                            </span>
                                                            <span className="text-xs text-gray-500">
                                                                Immagine non
                                                                disponibile dal
                                                                feed B2B
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>

                                                <CardContent className="space-y-5 p-6">
                                                    <div className="space-y-1">
                                                        <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                                                            SKU{" "}
                                                            {product.sku ||
                                                                product.code}
                                                        </div>
                                                        <div className="text-lg font-bold text-gray-900">
                                                            {product.name ||
                                                                product.code}
                                                        </div>
                                                        <div className="text-sm text-gray-500">
                                                            {product.code}
                                                        </div>
                                                    </div>

                                                    <p className="min-h-16 text-sm leading-6 text-gray-600">
                                                        {product.description ||
                                                            "Nessuna descrizione disponibile."}
                                                    </p>

                                                    <div className="space-y-3">
                                                        {colorBadges.length >
                                                            0 && (
                                                            <div className="flex flex-wrap gap-2">
                                                                {colorBadges.map(
                                                                    (badge) => (
                                                                        <span
                                                                            key={`${product.code}-color-${badge}`}
                                                                            className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                                                                            {
                                                                                badge
                                                                            }
                                                                        </span>
                                                                    ),
                                                                )}
                                                            </div>
                                                        )}

                                                        {temperatureBadges.length >
                                                            0 && (
                                                            <div className="flex flex-wrap gap-2">
                                                                {temperatureBadges.map(
                                                                    (badge) => (
                                                                        <span
                                                                            key={`${product.code}-temp-${badge}`}
                                                                            className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
                                                                            {
                                                                                badge
                                                                            }
                                                                        </span>
                                                                    ),
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="flex items-center justify-between gap-3 border-t border-gray-100 pt-4">
                                                        <div>
                                                            <div className="text-xs text-gray-500">
                                                                Prezzo unitario
                                                            </div>
                                                            <div className="text-xl font-bold text-gray-900">
                                                                {product.price}
                                                            </div>
                                                        </div>

                                                        <div className="flex gap-2">
                                                            <Button
                                                                variant="outline"
                                                                className="border-gray-200 text-gray-700"
                                                                onClick={() =>
                                                                    handleOpenPreview(
                                                                        product,
                                                                    )
                                                                }>
                                                                <Eye className="mr-2 h-4 w-4" />
                                                                Anteprima
                                                            </Button>
                                                            <Button
                                                                className="bg-[#0D6EFD] hover:bg-blue-700 text-white"
                                                                onClick={() =>
                                                                    handleAddToOrder(
                                                                        product,
                                                                    )
                                                                }>
                                                                <PackagePlus className="mr-2 h-4 w-4" />
                                                                Aggiungi
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        );
                                    })}
                                </div>
                            )}

                        {!isLoadingProducts &&
                            filteredProducts.length > 0 &&
                            viewMode === "table" && (
                                <>
                                    <div className="space-y-3 lg:hidden">
                                        {filteredProducts.map((product) => (
                                            <Card
                                                key={`mobile-${product.code}`}
                                                className="border-gray-100 shadow-sm">
                                                <CardContent className="space-y-4 p-4">
                                                    <div className="flex items-start justify-between gap-3">
                                                        <div className="min-w-0">
                                                            <div className="text-xs uppercase tracking-wide text-gray-500">
                                                                SKU{" "}
                                                                {product.sku ||
                                                                    product.code}
                                                            </div>
                                                            <div className="truncate text-base font-semibold text-gray-900">
                                                                {product.name ||
                                                                    product.code}
                                                            </div>
                                                            <div className="mt-1 text-sm text-gray-500">
                                                                {product.code}
                                                            </div>
                                                        </div>
                                                        <div className="text-right">
                                                            <div className="text-xs text-gray-500">
                                                                Prezzo
                                                            </div>
                                                            <div className="text-base font-semibold text-gray-900">
                                                                {product.price}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="flex flex-wrap gap-2">
                                                        {getVariantSummary(
                                                            product,
                                                        ).map((badge) => (
                                                            <span
                                                                key={`mobile-badge-${product.code}-${badge}`}
                                                                className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                                                                {badge}
                                                            </span>
                                                        ))}
                                                    </div>

                                                    <div className="flex gap-2">
                                                        <Button
                                                            variant="outline"
                                                            className="flex-1 border-gray-200 text-gray-700"
                                                            onClick={() =>
                                                                handleOpenPreview(
                                                                    product,
                                                                )
                                                            }>
                                                            <Eye className="mr-2 h-4 w-4" />
                                                            Anteprima
                                                        </Button>
                                                        <Button
                                                            className="flex-1 bg-[#0D6EFD] hover:bg-blue-700 text-white"
                                                            onClick={() =>
                                                                handleAddToOrder(
                                                                    product,
                                                                )
                                                            }>
                                                            <PackagePlus className="mr-2 h-4 w-4" />
                                                            Aggiungi
                                                        </Button>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </div>

                                    <div className="hidden overflow-hidden rounded-md border border-gray-100 lg:block">
                                        <Table>
                                            <TableHeader>
                                                <TableRow className="border-gray-100 bg-gray-50/50 hover:bg-transparent">
                                                    <TableHead className="py-2.5 text-xs font-semibold">
                                                        Prodotto
                                                    </TableHead>
                                                    <TableHead className="py-2.5 text-xs font-semibold">
                                                        SKU / Codice
                                                    </TableHead>
                                                    <TableHead className="py-2.5 text-xs font-semibold">
                                                        Brand / Categoria
                                                    </TableHead>
                                                    <TableHead className="py-2.5 text-xs font-semibold">
                                                        Varianti
                                                    </TableHead>
                                                    <TableHead className="py-2.5 text-xs font-semibold text-right">
                                                        Prezzo B2B
                                                    </TableHead>
                                                    <TableHead className="py-2.5 text-xs font-semibold text-right">
                                                        Azioni
                                                    </TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {filteredProducts.map(
                                                    (product) => (
                                                        <TableRow
                                                            key={product.code}
                                                            className="border-gray-100 hover:bg-slate-50/80 transition-colors">
                                                            <TableCell className="py-2">
                                                                <div className="flex items-center gap-3">
                                                                    <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
                                                                        {product.imageUrl &&
                                                                        !imageLoadErrors[
                                                                            product
                                                                                .code
                                                                        ] ? (
                                                                            <img
                                                                                src={
                                                                                    product.imageUrl
                                                                                }
                                                                                alt={
                                                                                    product.name ||
                                                                                    product.code
                                                                                }
                                                                                className="h-full w-full object-cover"
                                                                                onError={() =>
                                                                                    setImageLoadErrors(
                                                                                        (
                                                                                            prev,
                                                                                        ) => ({
                                                                                            ...prev,
                                                                                            [product.code]: true,
                                                                                        }),
                                                                                    )
                                                                                }
                                                                            />
                                                                        ) : (
                                                                            <div className="flex h-full w-full items-center justify-center text-gray-400">
                                                                                <Package className="h-4 w-4" />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <div className="space-y-0.5">
                                                                        <div className="font-semibold text-sm text-gray-900 leading-tight">
                                                                            {product.name ||
                                                                                product.code}
                                                                        </div>
                                                                        <div className="max-w-xs truncate text-xs text-gray-500">
                                                                            {product.description ||
                                                                                "Nessuna descrizione disponibile."}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </TableCell>
                                                            <TableCell className="py-2 text-xs text-gray-600">
                                                                <div className="font-mono font-medium text-gray-900">
                                                                    {product.sku ||
                                                                        product.code}
                                                                </div>
                                                                <div className="text-[11px] text-gray-400">
                                                                    {
                                                                        product.code
                                                                    }
                                                                </div>
                                                            </TableCell>
                                                            <TableCell className="py-2 text-xs">
                                                                <div className="space-y-1">
                                                                    {product.brandName && (
                                                                        <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
                                                                            {
                                                                                product.brandName
                                                                            }
                                                                        </span>
                                                                    )}
                                                                    {product
                                                                        .categories?.[0] && (
                                                                        <div className="text-[11px] text-gray-500">
                                                                            {
                                                                                product
                                                                                    .categories[0]
                                                                            }
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </TableCell>
                                                            <TableCell className="py-2">
                                                                <div className="flex flex-wrap gap-1">
                                                                    {getVariantSummary(
                                                                        product,
                                                                    ).map(
                                                                        (
                                                                            badge,
                                                                        ) => (
                                                                            <span
                                                                                key={`table-badge-${product.code}-${badge}`}
                                                                                className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-700">
                                                                                {
                                                                                    badge
                                                                                }
                                                                            </span>
                                                                        ),
                                                                    )}
                                                                </div>
                                                            </TableCell>
                                                            <TableCell className="py-2 text-right font-bold text-sm text-gray-900">
                                                                {product.price}
                                                            </TableCell>
                                                            <TableCell className="py-2">
                                                                <div className="flex justify-end gap-1.5">
                                                                    <Button
                                                                        variant="outline"
                                                                        size="sm"
                                                                        className="h-8 border-gray-200 text-xs text-gray-700 px-2.5"
                                                                        onClick={() =>
                                                                            handleOpenPreview(
                                                                                product,
                                                                            )
                                                                        }>
                                                                        <Eye className="mr-1 h-3.5 w-3.5" />
                                                                        Anteprima
                                                                    </Button>
                                                                    <Button
                                                                        size="sm"
                                                                        className="h-8 bg-[#0D6EFD] hover:bg-blue-700 text-white text-xs px-2.5"
                                                                        onClick={() =>
                                                                            handleAddToOrder(
                                                                                product,
                                                                            )
                                                                        }>
                                                                        <PackagePlus className="mr-1 h-3.5 w-3.5" />
                                                                        Aggiungi
                                                                    </Button>
                                                                </div>
                                                            </TableCell>
                                                        </TableRow>
                                                    ),
                                                )}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </>
                            )}

                        {/* Pagination Footer */}
                        {!isLoadingProducts && totalItems > 0 && (
                            <div className="flex flex-col sm:flex-row items-center justify-between pt-4 border-t border-gray-100 text-sm text-gray-500 gap-4">
                                <div>
                                    Mostrando{" "}
                                    {Math.min(
                                        (currentPage - 1) * productsPerPage + 1,
                                        totalItems,
                                    )}{" "}
                                    -{" "}
                                    {Math.min(
                                        currentPage * productsPerPage,
                                        totalItems,
                                    )}{" "}
                                    di {totalItems} prodotti
                                </div>

                                <div className="flex items-center gap-4">
                                    <div className="font-medium text-gray-900">
                                        Pagina {currentPage} di {totalPages}
                                    </div>

                                    <div className="flex items-center gap-1">
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            className="h-8 w-8 border-gray-200"
                                            disabled={currentPage <= 1}
                                            onClick={() => handlePageChange(1)}>
                                            <ChevronsLeft className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            className="h-8 w-8 border-gray-200"
                                            disabled={currentPage <= 1}
                                            onClick={() =>
                                                handlePageChange(
                                                    currentPage - 1,
                                                )
                                            }>
                                            <ChevronLeft className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            className="h-8 w-8 border-gray-200"
                                            disabled={currentPage >= totalPages}
                                            onClick={() =>
                                                handlePageChange(
                                                    currentPage + 1,
                                                )
                                            }>
                                            <ChevronRight className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            className="h-8 w-8 border-gray-200"
                                            disabled={currentPage >= totalPages}
                                            onClick={() =>
                                                handlePageChange(totalPages)
                                            }>
                                            <ChevronsRight className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                <ProductPreviewDialog
                    isOpen={isPreviewOpen}
                    product={selectedProduct}
                    colorBadges={selectedProductColors}
                    temperatureBadges={selectedProductTemperatures}
                    onOpenChange={setIsPreviewOpen}
                    onAddToOrder={handleAddToOrder}
                />

                <SelectCustomerModal
                    isOpen={isCustomerModalOpen}
                    product={productForCustomerSelect}
                    onOpenChange={setIsCustomerModalOpen}
                    onConfirm={handleConfirmCustomer}
                />
            </div>
        </DashboardLayout>
    );
}
